import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup, Alert, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from 'react-query';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { CME_USER_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';
import { messageError, messageToast, status200, DATE_TIME_REACT_WITHOUT_HOUR_FORMAT } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import { Button } from '../../../../components';
import DocumentInput from '../../../../components/document-input/DocumentInput';

const schema = yup.object().shape({
    noiDung: yup.string().required(messageError.emptyMessage).max(1000, 'Nội dung quá dài'),
    tieuMucId: yup.object().required(messageError.emptyMessage).nullable(),
    soLuong: yup.number().required(messageError.emptyMessage).min(1, 'Số lượng phải lớn hơn 0'),
    tuNgay: yup.date().required(messageError.emptyMessage),
    denNgay: yup.date().required(messageError.emptyMessage).min(yup.ref('tuNgay'), 'Ngày kết thúc phải sau ngày bắt đầu')
});

const CMEHoSoEdit = () => {
    const history = useHistory();
    const { id } = useParams();
    const [isLoadingSave, setIsLoadingSave] = useState(false);
    const [selectedRule, setSelectedRule] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    // Lấy dữ liệu hồ sơ hiện tại
    const { isLoading: isLoadingDetail, data: hoSoData } = useQuery(
        ['cmeHoSoDetail', id],
        () => Get(`${CME_USER_API.GET_BY_ID}/${id}`),
        {
            enabled: !!id,
            onSuccess: (data) => {
                if (data) {
                    const files = (data.dinhKemFiles?.$values || data.dinhKemFiles || [])
                        .map(f => ({ id: f.id, filePath: f.filePath, name: f.name }));
                    setUploadedFiles(files);
                }
            }
        }
    );

    // Lấy danh sách tiểu mục để chọn
    const { data: categoriesData } = useQuery(
        ['cmeCategoriesForSelect'],
        () => Get(CME_USER_API.GET_CATEGORIES)
    );

    const categories = categoriesData?.$values || categoriesData || [];

    // Tự động chọn Danh mục chính khi có dữ liệu hoSoData
    React.useEffect(() => {
        if (categories.length > 0 && hoSoData?.tieuMucId && !selectedCategory) {
            const foundCat = categories.find(cat => 
                (cat.cmeTieuMucs?.$values || cat.cmeTieuMucs || []).some(rule => rule.id === hoSoData.tieuMucId)
            );
            if (foundCat) {
                setSelectedCategory({
                    label: foundCat.tenDanhMuc,
                    value: foundCat.id,
                    subItems: foundCat.cmeTieuMucs?.$values || foundCat.cmeTieuMucs || []
                });
            }
        }
    }, [categories, hoSoData, selectedCategory]);

    const categoryOptions = categories.map(cat => ({
        label: cat.tenDanhMuc,
        value: cat.id,
        subItems: cat.cmeTieuMucs?.$values || cat.cmeTieuMucs || []
    }));

    const ruleOptions = selectedCategory 
        ? selectedCategory.subItems.map(rule => ({
            label: `${rule.tenTieuMuc}${rule.vaiTro ? ` [${rule.vaiTro}]` : ''}`,
            value: rule.id,
            dinhMucGio: rule.dinhMucGio,
            donViTinh: rule.donViTinh,
            vaiTro: rule.vaiTro,
            ghiChu: rule.ghiChu
        }))
        : [];

    const submitFunc = useMutation((value) => Post(CME_USER_API.SUBMIT_HO_SO, null, value));

    const handleUpdate = (values) => {
        setIsLoadingSave(true);
        const model = {
            id: id,
            tieuMucId: values.tieuMucId.value,
            noiDung: values.noiDung,
            soLuong: Number(values.soLuong) || 1,
            diaDiem: values.diaDiem || null,
            tuNgay: values.tuNgay,
            denNgay: values.denNgay,
            namTichLuy: values.tuNgay.getFullYear(),
            fileIds: uploadedFiles.map(f => f.id).filter(id => !!id)
        };

        submitFunc.mutate(model, {
            onSuccess: async (res) => {
                setIsLoadingSave(false);
                if (res.ok) {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    history.push(URI_INTERNAL.CMECA_NHAN_PATH);
                } else {
                    toast.error(messageToast.messageToastUpdateError);
                }
            },
            onError: (err) => {
                setIsLoadingSave(false);
                toast.error(err?.message || err?.title || "Lỗi khi cập nhật hồ sơ");
            }
        });
    };

    if (isLoadingDetail) {
        return (
            <div className="container-fluid text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Đang tải dữ liệu hồ sơ...</p>
            </div>
        );
    }

    // Tìm option đã chọn trước đó
    const currentRuleOption = ruleOptions.find(opt => opt.value === hoSoData?.tieuMucId) || null;

    const initialValues = {
        noiDung: hoSoData?.noiDung || '',
        tieuMucId: currentRuleOption,
        soLuong: hoSoData?.soLuong || 1,
        diaDiem: hoSoData?.diaDiem || '',
        tuNgay: hoSoData?.tuNgay ? new Date(hoSoData.tuNgay) : new Date(),
        denNgay: hoSoData?.denNgay ? new Date(hoSoData.denNgay) : new Date()
    };

    return (
        <div className="container-fluid">
            <Card className="shadow-sm">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 text-primary"><i className="fas fa-edit mr-2"></i>Chỉnh sửa hồ sơ tích lũy CME</h5>
                </Card.Header>
                <Card.Body>
                    {hoSoData?.lyDoTuChoi && (
                        <Alert variant="danger" className="mb-4">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            <strong>Lý do từ chối lần trước:</strong> {hoSoData.lyDoTuChoi}
                        </Alert>
                    )}

                    <Formik
                        validationSchema={schema}
                        onSubmit={handleUpdate}
                        initialValues={initialValues}
                        enableReinitialize
                    >
                        {({ handleSubmit, handleChange, values, errors, setFieldValue, touched }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                {/* Nội dung hoạt động */}
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Nội dung học tập / Hoạt động <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="noiDung"
                                                value={values.noiDung}
                                                onChange={handleChange}
                                                isInvalid={touched.noiDung && !!errors.noiDung}
                                                placeholder="VD: Tham gia hội thảo Y khoa tim mạch toàn quốc 2024..."
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.noiDung}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Loại hình / Danh mục CME */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Danh mục chính <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                placeholder="Chọn loại hình..."
                                                value={selectedCategory}
                                                options={categoryOptions}
                                                onChange={(opt) => {
                                                    setSelectedCategory(opt);
                                                    setFieldValue('tieuMucId', null);
                                                    setSelectedRule(null);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Tiểu mục / Vai trò <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                placeholder={selectedCategory ? "Chọn quy định tính điểm..." : "Hãy chọn danh mục trước..."}
                                                isDisabled={!selectedCategory}
                                                value={values.tieuMucId}
                                                options={ruleOptions}
                                                onChange={(opt) => {
                                                    setFieldValue('tieuMucId', opt);
                                                    setSelectedRule(opt);
                                                }}
                                                className={touched.tieuMucId && errors.tieuMucId ? 'is-invalid' : ''}
                                            />
                                            {touched.tieuMucId && errors.tieuMucId && (
                                                <div className="text-danger small mt-1">{errors.tieuMucId}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Thông tin định mức */}
                                {(selectedRule || currentRuleOption) && (
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Alert variant="info" className="py-2">
                                                <i className="fas fa-info-circle mr-2"></i>
                                                <strong>Định mức:</strong> {(selectedRule || currentRuleOption).dinhMucGio} giờ/{(selectedRule || currentRuleOption).donViTinh || 'đơn vị'}
                                                {(selectedRule || currentRuleOption).vaiTro && <span className="ml-3">| <strong>Vai trò:</strong> {(selectedRule || currentRuleOption).vaiTro}</span>}
                                                <br/>
                                                <small className="text-muted">Số giờ tín chỉ = Số lượng × {(selectedRule || currentRuleOption).dinhMucGio} = <strong>{(values.soLuong * (selectedRule || currentRuleOption).dinhMucGio).toFixed(1)} giờ</strong></small>
                                            </Alert>
                                        </Col>
                                    </Row>
                                )}

                                {/* Số lượng + Địa điểm */}
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Số lượng <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="soLuong"
                                                min={1}
                                                value={values.soLuong}
                                                onChange={handleChange}
                                                isInvalid={touched.soLuong && !!errors.soLuong}
                                            />
                                            <Form.Text className="text-muted">VD: 2 buổi, 1 bài báo...</Form.Text>
                                            <Form.Control.Feedback type="invalid">{errors.soLuong}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={8}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Địa điểm</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="diaDiem"
                                                value={values.diaDiem}
                                                onChange={handleChange}
                                                placeholder="VD: Bệnh viện TW Quân đội 108, Hà Nội"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Từ ngày - Đến ngày */}
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Từ ngày <span className="text-danger">*</span></Form.Label>
                                            <div className="d-block">
                                                <DatePicker
                                                    selected={values.tuNgay}
                                                    onChange={(date) => setFieldValue('tuNgay', date)}
                                                    dateFormat={DATE_TIME_REACT_WITHOUT_HOUR_FORMAT}
                                                    className="form-control"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Đến ngày <span className="text-danger">*</span></Form.Label>
                                            <div className="d-block">
                                                <DatePicker
                                                    selected={values.denNgay}
                                                    onChange={(date) => setFieldValue('denNgay', date)}
                                                    dateFormat={DATE_TIME_REACT_WITHOUT_HOUR_FORMAT}
                                                    className="form-control"
                                                />
                                            </div>
                                            {touched.denNgay && errors.denNgay && (
                                                <div className="text-danger small mt-1">{errors.denNgay}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">Năm tích lũy</Form.Label>
                                            <Form.Control
                                                type="text"
                                                disabled
                                                value={values.tuNgay ? values.tuNgay.getFullYear() : new Date().getFullYear()}
                                            />
                                            <Form.Text className="text-muted">Tự động lấy từ ngày bắt đầu</Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* File minh chứng */}
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="font-weight-bold">File minh chứng (Chứng chỉ, chứng nhận...)</Form.Label>
                                            <DocumentInput
                                                multiple={true}
                                                value={uploadedFiles}
                                                onFilesChange={(files) => setUploadedFiles(files)}
                                                labelTitle="Danh sách minh chứng"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col md={12} className="text-right">
                                        <hr />
                                        <ButtonGroup>
                                            <Button type="submit" theme="primary" className="px-4" disabled={isLoadingSave}>
                                                {isLoadingSave ? 'Đang lưu...' : 'Cập nhật hồ sơ'}
                                            </Button>
                                            <Button variant="secondary" className="ml-2 px-4" onClick={() => history.push(URI_INTERNAL.CMECA_NHAN_PATH)}>
                                                Hủy bỏ
                                            </Button>
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>
        </div>
    );
};

export default CMEHoSoEdit;
